"use client";

import React, { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { Database, FileSearch, Sparkles, Globe, CheckCircle2 } from "lucide-react";
import { KnowledgeCheckpoint } from "@/agents/shared/knowledgeStreamClient";
import voiceFillerEngine from "@/agents/shared/voiceFillerEngine";

interface AgentKnowledgeCheckpointHudProps {
    className?: string;
    onStatusChange?: (isActive: boolean) => void;
}

const STAGE_CONFIGS: Record<number, { icon: React.ComponentType<{ className?: string }>; color: string; bg: string; pulseColor: string }> = {
    1: { icon: Database, color: "text-sky-400", bg: "bg-sky-500/20", pulseColor: "border-sky-500/40" },
    2: { icon: FileSearch, color: "text-indigo-400", bg: "bg-indigo-500/20", pulseColor: "border-indigo-500/40" },
    3: { icon: Sparkles, color: "text-amber-400", bg: "bg-amber-500/20", pulseColor: "border-amber-500/40" },
    4: { icon: Globe, color: "text-emerald-400", bg: "bg-emerald-500/20", pulseColor: "border-emerald-500/40" },
    5: { icon: CheckCircle2, color: "text-emerald-300", bg: "bg-emerald-500/20", pulseColor: "border-emerald-500/40" },
};

function formatNaturalStepLabel(rawLabel?: string): string {
    if (!rawLabel) return "Em đang xử lý thông tin...";
    let text = rawLabel;
    text = text.replace(/^Đang đối chiếu tài liệu:\s*/i, "Em đang đọc tài liệu: ");
    text = text.replace(/^Đang phân tích ngữ cảnh và mở rộng từ khóa.*$/i, "Em đang phân tích sâu ngữ cảnh câu hỏi...");
    text = text.replace(/^Đang tra cứu sâu với từ khóa mở rộng.*$/i, "Em đang tra cứu sâu với từ khóa chuyên ngành...");
    text = text.replace(/^Đang tra cứu trên website chính thức\s*/i, "Em đang tra cứu trên cổng thông tin ");
    text = text.replace(/^Đang kết nối cơ sở tri thức.*$/i, "Em đang mở cơ sở tri thức dự án...");
    text = text.replace(/^Khớp câu trả lời tức thì.*$/i, "Em đã tìm thấy câu trả lời tức thì!");
    text = text.replace(/^Hoàn tất tra cứu$/i, "Đã tìm thấy thông tin chính xác!");
    return text;
}

export const AgentKnowledgeCheckpointHud: React.FC<AgentKnowledgeCheckpointHudProps> = ({
    className = "",
    onStatusChange,
}) => {
    const [checkpoint, setCheckpoint] = useState<KnowledgeCheckpoint | null>(null);
    const [isVisible, setIsVisible] = useState(false);
    const [isFadingOut, setIsFadingOut] = useState(false);
    const [hasChatDock, setHasChatDock] = useState(false);
    const [mounted, setMounted] = useState(false);

    const isVisibleRef = useRef(false);
    const lastPlayedStageRef = useRef<number>(0);
    const dismissTimerRef = useRef<NodeJS.Timeout | null>(null);

    useEffect(() => {
        setMounted(true);
    }, []);

    useEffect(() => {
        isVisibleRef.current = isVisible;
        onStatusChange?.(isVisible);
    }, [isVisible, onStatusChange]);

    useEffect(() => {
        const handleEvent = (event: Event) => {
            const data = (event as CustomEvent<KnowledgeCheckpoint>).detail;
            if (!data) return;

            setCheckpoint(data);
            setHasChatDock(Boolean(document.querySelector('[data-agent-chat-input="true"]')));

            // Voice filler trigger: Stage 1 (Init search) & Stage 4 (Whitelist search)
            if (data.stage === 1 && lastPlayedStageRef.current === 0) {
                lastPlayedStageRef.current = 1;
                voiceFillerEngine.speak('knowledge_init');
            } else if (data.stage === 4 && lastPlayedStageRef.current < 4) {
                lastPlayedStageRef.current = 4;
                voiceFillerEngine.speak('whitelist_web', data.detail?.domain);
            }

            if (data.isCompleted || data.stage >= 5) {
                lastPlayedStageRef.current = 0;
                if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
                dismissTimerRef.current = setTimeout(() => {
                    if (isVisibleRef.current) {
                        setIsFadingOut(true);
                        setTimeout(() => {
                            setIsVisible(false);
                            setIsFadingOut(false);
                            setCheckpoint(null);
                        }, 400);
                    } else {
                        setCheckpoint(null);
                    }
                }, 1500);
                return;
            }

            setIsVisible(true);
            setIsFadingOut(false);

            if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
            dismissTimerRef.current = setTimeout(() => {
                setIsFadingOut(true);
                voiceFillerEngine.cancel();
                setTimeout(() => {
                    setIsVisible(false);
                    setIsFadingOut(false);
                    setCheckpoint(null);
                }, 300);
            }, 15000);
        };

        window.addEventListener("sg3d:knowledge-checkpoint", handleEvent);
        return () => {
            window.removeEventListener("sg3d:knowledge-checkpoint", handleEvent);
            if (dismissTimerRef.current) clearTimeout(dismissTimerRef.current);
            voiceFillerEngine.cancel();
        };
    }, []);

    if (!mounted || !isVisible || !checkpoint) return null;

    const currentStage = Math.min(Math.max(checkpoint.stage || 1, 1), 5);
    const config = STAGE_CONFIGS[currentStage] || STAGE_CONFIGS[1];
    const IconComponent = config.icon;
    const naturalLabel = formatNaturalStepLabel(checkpoint.label);

    const positionClasses = hasChatDock
        ? "fixed bottom-[205px] sm:bottom-[215px] md:bottom-[220px] right-3 sm:right-6 md:right-8 z-[9999]"
        : "fixed bottom-[185px] sm:bottom-[195px] md:bottom-[205px] right-3 sm:right-6 md:right-8 z-[9999]";

    const hudContent = (
        <div
            className={`pointer-events-none transition-all duration-300 ease-out select-none ${positionClasses} ${
                isFadingOut ? "opacity-0 -translate-y-2 scale-95" : "opacity-100 translate-y-0 scale-100"
            } ${className}`}
            role="status"
            aria-live="polite"
        >
            <div className="flex items-center gap-2.5 px-4 py-2 rounded-full bg-[#0c121e]/90 hover:bg-[#0c121e]/95 border border-white/20 shadow-[0_20px_50px_rgba(0,0,0,0.85)] text-white max-w-[calc(100vw-1.5rem)] sm:max-w-[420px]">
                <div className={`relative flex items-center justify-center w-7 h-7 rounded-full shrink-0 ${config.bg} ${config.color}`}>
                    <span className={`absolute inset-0 rounded-full animate-ping opacity-30 border ${config.pulseColor}`} />
                    <IconComponent className="w-4 h-4 relative z-10 animate-pulse shrink-0" />
                </div>

                <div className="flex flex-col min-w-0 pr-1 flex-1">
                    <span className="text-xs font-semibold text-white tracking-normal truncate">
                        {naturalLabel}
                    </span>
                    {checkpoint.detail?.source && (
                        <span className="text-[10px] text-blue-300 truncate">
                            {checkpoint.detail.source}
                        </span>
                    )}
                </div>

                <div className="flex items-center gap-1.5 pl-2 border-l border-white/15 shrink-0">
                    {[1, 2, 3, 4].map((step) => (
                        <span
                            key={step}
                            className={`w-1.5 h-1.5 rounded-full transition-all duration-300 ${
                                currentStage >= step
                                    ? "bg-sky-400 scale-125 shadow-sm shadow-sky-400/80"
                                    : "bg-white/20"
                            }`}
                        />
                    ))}
                </div>
            </div>
        </div>
    );

    return createPortal(hudContent, document.body);
};

export default AgentKnowledgeCheckpointHud;
